import { QueryClient } from '@tanstack/react-query';

import { DayType, EventProps } from 'features/Home/components/Events/types';
import { QUERY_KEYS_NO_FETCHING } from 'utils/constants';
import { camelizeKeys } from 'humps';

import { getData, post } from 'services/api';
import {
	Available,
	CompIssued,
	NestedTicketObject,
	RecipientProps,
	RecipientTicketType,
	SendTicketsDateProps,
	SendTicketsEventProps,
	SendTicketsFunctionProps,
} from './types';
import { convertObject, formatDate } from './utils';
import { generateNoEmailPlaceholder } from 'utils/utils';

/**
 * Single post-send cache invalidation, shared by EVERY send path (single, seated, bulk) so none can
 * silently leave stale numbers behind when the manager stays on the page via "Send more". Refetches:
 *  - `['orders']` (prefix) — the Comped Tickets list the page navigates back to.
 *  - the three capacity sources the on-page tiles read through `useEventCapacity` — public ticket types,
 *    event pools, and the event highlights — so the availability and pool tiles reflect the just-sent comps.
 * `id` is the event id; an empty/undefined id is a no-op-safe key (the underlying queries are `enabled: !!id`).
 */
export const invalidateAfterSend = (queryClient: QueryClient, id: string): void => {
	queryClient.refetchQueries(['orders']);
	queryClient.refetchQueries(['ticketTypes', id]);
	queryClient.refetchQueries(['eventPools', id]);
	queryClient.refetchQueries([QUERY_KEYS_NO_FETCHING.EVENT_HIGHLIGHTS, id]);
};

export const getEvent = async (id: string): Promise<SendTicketsEventProps> => {
	const res: EventProps = await getData(`events/${id}`);

	const dates: SendTicketsDateProps[] = [];
	const ticketTypes: RecipientTicketType[] = [];
	res.days.forEach(({ start_date, end_date, id }: DayType) => {
		const formatedDates = formatDate({ startDate: start_date, timezone: res.time_zone, endDate: end_date });
		dates.push({
			time: { label: formatedDates.time, value: `${id}` },
			day: { label: formatedDates.day, value: `${id}` },
		});
	});
	res.ticket_types.forEach((tt) => {
		const { name, amount, ID, initial_price, sold, sold_out } = tt;
		// `seats_category_key` is present on the API row when the partner uses seats.io and the
		// ticket type is bound to a category. The shared EventProps.TicketType in
		// features/Home/components/Events/types.ts does not declare it, so read it via a
		// narrow cast — the field is camelized at the seated-form layer when used.
		const seatsCategoryKey = (tt as unknown as { seats_category_key?: string }).seats_category_key;
		ticketTypes.push({
			type: name,
			quantity: 0,
			amount,
			ticketTypeId: ID,
			ticket_type_id: ID,
			initialPrice: initial_price,
			soldOut: sold_out,
			sold,
			seatsCategoryKey: seatsCategoryKey ? String(seatsCategoryKey) : undefined,
		});
	});
	return {
		name: res.name,
		id: res.id,
		ticketTypes,
		days: dates,
		timezone: res.time_zone,
		streetAddress: res.street_address,
		seatsEventKey: res.seats_event_key,
	};
};

export const checkTicketsAvailability = (tickets: NestedTicketObject): Promise<Available> => {
	const body = convertObject(tickets);
	return post('/public/ticketing/ticket_types/availability', body);
};

// Falls back to the shipped route when the versioned twin is not deployed yet, reporting no ids.
export const sendTickets = async ({ recipients }: SendTicketsFunctionProps): Promise<CompIssued[]> => {
	const body = recipients.map((recipient: RecipientProps) => ({
		recipient_name: recipient.firstName,
		recipient_last_name: recipient.lastName,
		email: recipient.noEmail ? generateNoEmailPlaceholder() : recipient.email,
		ticket_types: recipient.ticketTypes,
	}));

	try {
		const response = await post('tickets/comp-tickets/v1', body);
		return camelizeKeys((response as { comps?: unknown[] })?.comps ?? []) as unknown as CompIssued[];
	} catch (error) {
		if (!isRouteMissing(error)) throw error;
		await post('tickets/comp-tickets', body);
		return [];
	}
};

const isRouteMissing = (error: unknown): boolean =>
	(error as { response?: { status?: number } })?.response?.status === 404;
